// Generated by tutorials/record.cjs from tutorials/articles/h77-corporate-dashboard.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h77 --text-only` (wording) or
// `node tutorials/record.cjs h77` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'corporate-dashboard',
  catalogId: 'h77',
  title: 'The Corporate Dashboard',
  summary: 'For corporate offices: see every center\'s census and activity side by side, spot the ones that need a look, open any center\'s details, and check which centers serve the shared menu as planned.',
  audience: 'Corporate and regional staff who look after several centers',
  area: 'home',
  learn: [
    'Open the Corporate Dashboard from your corporate facility',
    'Read each center\'s census, occupancy and activity',
    'Spot the centers that need a look',
    'Open a center\'s details, or its own dashboard',
    'Check which centers serve the shared menu as planned',
  ],
  keywords: [
    'corporate',
    'corporate dashboard',
    'corporate office',
    'regional',
    'multi site',
    'multi-site',
    'centers',
    'all facilities',
    'occupancy',
    'census',
    'roll up',
    'rollup',
    'district',
    'menu compliance',
    'shared menu',
    'deviation',
    'ehr stuck',
    'weight alerts',
    'overdue weigh-ins',
  ],
  steps: [
    {
      id: 'open',
      part: 'Read every center at a glance',
      heading: 'Open the Corporate Dashboard',
      text: 'Sign in to your corporate facility (Switch Facility, at the top). Your Dashboard opens as the Corporate Dashboard. It is also in the menu: select Home, then Corporate Dashboard.\n\nThe Census & Activity tab opens with the totals for all your centers: residents, guests, beds, the average occupancy, and the admissions and discharges in the time span.',
      media: {
        image: {
          src: '/help/corporate-dashboard/open.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/corporate-dashboard/open.mp4',
          width: 1280,
          height: 720,
          durationSec: 6,
        },
        alt: 'The Corporate Dashboard on Census & Activity: Total Residents, Total Guests, Total Beds, Avg Occupancy, Admissions, Discharges and EHR blocked, above a row for each center.',
      },
    },
    {
      id: 'alerts',
      part: 'Read every center at a glance',
      heading: 'See what needs a look',
      text: 'The yellow band names the centers past a limit: occupancy at 95% or more, or 10 or more residents with an overdue weigh-in.\n\nThe EHR blocked box appears only when a center has EHR messages that could not be applied.',
      media: {
        image: {
          src: '/help/corporate-dashboard/alerts.webp',
          width: 1280,
          height: 516,
        },
        alt: 'The band: 1 facility over occupancy threshold and 1 facility with overdue weigh-ins, and the EHR blocked box.',
      },
    },
    {
      id: 'grid',
      part: 'Read every center at a glance',
      heading: 'Compare the centers',
      text: 'Each center has a row: Beds, Residents, Guests and Occupancy %, then the Admissions, Discharges, Diet Changes and Allergy Changes in the time span, Wt Alerts and Tube Feeds.\n\nOccupancy has a hollow triangle from 80% and a solid one from 95%. Wt Alerts is orange when there are any, and red from 10.',
      media: {
        image: {
          src: '/help/corporate-dashboard/grid.webp',
          width: 1280,
          height: 720,
        },
        alt: 'The center rows: Hillcrest at 95.5% occupancy with a solid triangle, and Riverside with 12 weight alerts in red.',
      },
    },
    {
      id: 'span',
      part: 'Read every center at a glance',
      heading: 'Change the time span',
      text: 'Time span, at the top right, sets how far back the activity columns look: 12 hours up to 7 days. Group by District, beside it, puts the centers under their districts with subtotals, and Refresh fetches the latest numbers.',
      media: {
        image: {
          src: '/help/corporate-dashboard/span.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/corporate-dashboard/span.mp4',
          width: 1280,
          height: 720,
          durationSec: 6,
        },
        alt: 'Time span set to 7 days, with Group by District and Refresh beside it.',
      },
    },
    {
      id: 'ehr',
      part: 'Read every center at a glance',
      heading: 'EHR Stuck',
      text: 'EHR Stuck, at the end of the row, counts the messages from a center\'s EHR that could not be applied, with the age of the oldest. A diet order in one of them never reached the resident, so call that center.',
      media: {
        image: {
          src: '/help/corporate-dashboard/ehr.webp',
          width: 1280,
          height: 720,
        },
        alt: 'The centers grouped by district, and the EHR Stuck column: Hillcrest with 3 and 4d, Riverside with 2.',
      },
    },
    {
      id: 'details',
      part: 'Look closer at one center',
      heading: 'Open a center',
      text: 'Select a center\'s row. Its details open: the census with its most common diet types, the activity in the time span, its weight alerts by resident, and its recent admissions.',
      media: {
        image: {
          src: '/help/corporate-dashboard/details.webp',
          width: 1280,
          height: 888,
        },
        clip: {
          src: '/help/corporate-dashboard/details.mp4',
          width: 1280,
          height: 888,
          durationSec: 2.2,
        },
        alt: 'The Maple Grove details: Census, Activity, Weight Alerts with three residents, and Recent Admissions.',
      },
    },
    {
      id: 'full',
      part: 'Look closer at one center',
      heading: 'Go to its dashboard',
      text: 'Open Full Dashboard switches you to that center and opens its own dashboard, where you can open any resident. Switch back to your corporate facility to return to the Corporate Dashboard.',
      media: {
        image: {
          src: '/help/corporate-dashboard/full.webp',
          width: 1280,
          height: 888,
        },
        alt: 'The Open Full Dashboard button at the bottom of the details window, marked.',
      },
    },
    {
      id: 'menus',
      part: 'Check menu compliance',
      heading: 'Open Menu Compliance',
      text: 'Close the details and select the Menu Compliance tab. It checks each center\'s active menu cycle against the menu you shared with it.\n\nThe boxes count the centers On Template, with a Minor Deviation or a Major Deviation, and Not Shared.',
      media: {
        image: {
          src: '/help/corporate-dashboard/menus.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/corporate-dashboard/menus.mp4',
          width: 1280,
          height: 720,
          durationSec: 4.4,
        },
        alt: 'Menu Compliance: Total Facilities 5, On Template 2, Minor Deviation 1, Major Deviation 1, Not Shared 1, and a row for each center.',
      },
    },
    {
      id: 'deviation',
      part: 'Check menu compliance',
      heading: 'Read the deviation',
      text: 'Deviation % is how much of the shared menu the center has changed. 0% is On Template, up to 20% a Minor Deviation, and over 20% a Major Deviation. Template Ver. is the version they have, and Shared On when they got it.\n\nNot Shared means the center runs a menu of its own.',
      media: {
        image: {
          src: '/help/corporate-dashboard/deviation.webp',
          width: 1280,
          height: 720,
        },
        alt: 'Hillcrest with Major Deviation at 26% on template version 2, Riverside with Minor Deviation at 8%, and Lakeview Not Shared.',
      },
    },
  ],
  tips: [
    'Select a column heading to sort by it: sort EHR Stuck or Wt Alerts to put the centers that need a call at the top.',
    'Group by District puts the centers under their district, with each district\'s totals.',
    'Your Time span and grouping are remembered for next time.',
  ],
  notes: [
    'Corporate Dashboard is in the menu only while you are signed in to your corporate facility, and it needs Reports on your role. Switch facility to your corporate office to see it.',
    'It covers the centers linked to your corporate facility that you have access to. If a center is missing, ask your administrator to give you access to it.',
    'EHR Stuck counts messages from a center\'s EHR that could not be applied, for example a resident who could not be matched or a value that is not mapped. They do not clear on their own: someone at that center needs to look at its EHR page.',
  ],
  related: ['your-dashboard', 'switching-facility', 'ehr-page', 'synced-rows-and-push-to'],
  recorded: '2026-11-03',
};

export default article;
